import React, {useState, useEffect} from 'react'
import {useStaticQuery, graphql, Link} from 'gatsby'
import {PropTypes} from 'prop-types'

import {css} from '@emotion/react'
import {convertDatoCmsImgPathsToLocal} from '../util/datoCmsDataFactory'
import {colors, globalStylesProtection} from '../constants'
import {logDplWithExtensionParams, EVENTS} from '../util/dpl'
import Layout from '../components/layout'
import SEO from '../components/seo'
import Button from '../components/templates/protection-v1/button'
import HamburgerMenu from '../components/HamburgerMenu'
import Footer from '../components/templates/protection-v1/Footer'
import SurveyUninstall from '../components/SurveyUninstall'

import StartpageLogoRedesign from '../images/startpage-logo-redesign.svg'

const CONTAINER_STYLES = `
    width: 1200px;
    margin: auto;
    padding: 0 1rem;
`

const ProtectionUninstall = ({languageCode = 'en'}) => {
    const site = useStaticQuery(graphql`
        query ProtectionUninstallPage {
            site {
                siteMetadata {
                    ...ProtectionUninstall
                }
            }
        }
    `)
    const {
        hamburgerLinks,
        uninstallSection1Image: {url: heroImageUrl = ``},
        uninstallSection1Title,
        uninstallSection1ReinstallButtonText,
        uninstallSection2SurveyButtonText,
        uninstallSection2Title,
        uninstallSection2SurveyCompleteTitle,
        uninstallSection2SurveyCompleteDescription,
        uninstallSection2SurveyOptions,
        footerBlogAsset,
        footerLinks,
    } = site.site.siteMetadata.privacyprotect[languageCode]
    const homeLink = `https://www.startpage.com/${languageCode}`

    const [hasFiredUinstall, setHasFiredUinstall] = useState(false)

    useEffect(() => {
        if (!hasFiredUinstall) {
            logDplWithExtensionParams(
                EVENTS.StartpagePageLoad, // event
                {}, // payload
                ['act', 'sgc', 'experiment'], // denylist
            )
            setHasFiredUinstall(true)
        }
    }, [hasFiredUinstall])

    return (
        <Layout globalStyles={globalStylesProtection}>
            <SEO title={uninstallSection1Title} description={uninstallSection2Title} />
            <section
                css={css`
                    color: ${colors.midnight};
                    height: 100vh;
                `}
            >
                <section
                    className="header"
                    css={css`
                        background: ${colors.whisper};
                    `}
                >
                    <div
                        css={css`
                            ${CONTAINER_STYLES}
                            padding: 3rem 1rem;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                        `}
                    >
                        <a href={homeLink} rel="noopener noreferrer" tabIndex="0">
                            <StartpageLogoRedesign />
                        </a>
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                .reinstallButton {
                                    margin-right: 2rem;
                                }
                            `}
                        >
                            <Link to={`/${languageCode}/protection`}>
                                <Button
                                    text={uninstallSection1ReinstallButtonText}
                                    onClick={() => {}}
                                    overrideStyles={`
                                        color: ${colors.brandBlue};
                                        border: 1px solid ${colors.brandBlue};
                                        border-radius: 4px;
                                        padding: 0.5rem 1rem;
                                        background: transparent;
                                        font-weight: 500;
                                        margin-right: 2rem;
                                        &:hover {
                                            color: #fff;
                                            background: ${colors.brandBlue};
                                            cursor: pointer;
                                        }
                                    `}
                                    tabIndex="0"
                                />
                            </Link>
                            <HamburgerMenu links={hamburgerLinks} />
                        </div>
                    </div>
                </section>

                <section
                    className="hero"
                    css={css`
                        background: ${colors.whisper};
                    `}
                >
                    <div
                        css={css`
                            ${CONTAINER_STYLES}
                            position: relative;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            position: relative;
                            h1 {
                                font-size: 38px;
                                font-weight: 600;
                                padding: 3rem 0 8rem;
                            }
                        `}
                    >
                        {/* these divs are like a confeti texture in the bg */}
                        <div
                            css={css`
                                height: 40px;
                                width: 40px;
                                background: ${colors.whisperDark};
                                position: absolute;
                                opacity: 0.5;
                                left: 29rem;
                                top: 11rem;
                            `}
                        />
                        <div
                            css={css`
                                height: 64px;
                                width: 64px;
                                border: 2px solid ${colors.brandBlueLight};
                                position: absolute;
                                left: 30rem;
                                top: 12rem;
                            `}
                        />
                        <div
                            css={css`
                                height: 40px;
                                width: 40px;
                                border: 2px solid ${colors.whisperDark};
                                position: absolute;
                                left: 36rem;
                                top: 6rem;
                            `}
                        />
                        <div
                            css={css`
                                height: 64px;
                                width: 64px;
                                border: 2px solid ${colors.whisperDark};
                                position: absolute;
                                right: -2rem;
                                top: 4rem;
                            `}
                        />
                        <h1>{uninstallSection1Title}</h1>
                        <div
                            css={css`
                                flex: 0 0 50%;
                                img {
                                    position: absolute;
                                    bottom: 0;
                                }
                            `}
                        >
                            <img src={convertDatoCmsImgPathsToLocal(heroImageUrl)} alt="sad computer" />
                        </div>
                    </div>
                </section>

                <section
                    className="survey"
                    css={css`
                        ${CONTAINER_STYLES}
                        padding: 4rem 1rem;
                    `}
                >
                    <SurveyUninstall
                        title={uninstallSection2Title}
                        surveyCompleteTitle={uninstallSection2SurveyCompleteTitle}
                        surveyCompleteDescription={uninstallSection2SurveyCompleteDescription}
                        optionData={uninstallSection2SurveyOptions}
                        submitButtonText={uninstallSection2SurveyButtonText}
                        reinstallButtonText={uninstallSection1ReinstallButtonText}
                        ctaType=""
                        languageCode={languageCode}
                    />
                </section>
                <Footer languageCode={languageCode} links={footerLinks} footerBlogAsset={footerBlogAsset} />
            </section>
        </Layout>
    )
}

ProtectionUninstall.propTypes = {
    languageCode: PropTypes.string,
}

ProtectionUninstall.defaultProps = {
    languageCode: `en`,
}

export default ProtectionUninstall
